/**
 * FutureClosureEdgeCases - Advanced Edge Case Overrides
 * 
 * Collapsible section for handling forensic accounting edge cases:
 * - Revenue spike detection and handling
 * - Accounting basis (accrual/cash) with lag
 * - Payroll classification (fixed/variable + ICW)
 * - Entity type and expense policies
 * - Data gap warnings
 * - Shutdown/restart cost overrides
 * - Multiplier settings
 */

import React, { useState, useEffect } from 'react';
import { 
  ChevronDown, ChevronUp, AlertTriangle, Settings, 
  TrendingUp, DollarSign, Users, Building, Calendar,
  AlertCircle, Info, HelpCircle, FileText, Sparkles, Loader2
} from 'lucide-react';
import { EdgeCaseConfig, EdgeCaseFlags, EdgeCaseAnalysisResult, analyzeEdgeCases, generateEdgeCaseExplanation, EdgeCaseExplanationResult } from '../services/futureClosureApi';

// Type definitions for granular details
interface GrowthMonth {
  month: number;
  month_name: string;
  growth_pct: number;
}

// Tooltip component for edge case explanations
const Tooltip: React.FC<{ text: string; children: React.ReactNode }> = ({ text, children }) => (
  <div className="group relative inline-flex items-center">
    {children}
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50 max-w-xs">
      {text}
      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
    </div>
  </div>
);

// Tooltip content for each edge case
const TOOLTIPS = {
  revenueSpike: "A >25% year-over-year revenue increase may indicate a one-time event (disaster cleanup, special contract). Excluding it provides a more sustainable baseline for projections.",
  quarterExclusion: "Surgically normalize only the spiked quarters while preserving legitimate seasonality in other quarters.",
  accountingBasis: "Cash-basis businesses record revenue when payment is received, which can lag 15-45 days behind actual service delivery. Adjusting for this lag improves accuracy.",
  payrollMode: "Fixed payroll assumes all staff continue during closure (typical for short closures). Variable assumes non-essential staff are suspended (typical for long closures).",
  icwPayroll: "Increased Cost of Working (ICW) classifies continued payroll as an extra expense incurred to minimize loss, rather than a continuing cost.",
  entityType: "Different business types have different expense structures. Nonprofits typically have 80% variable expenses due to donation volatility.",
  shutdownCosts: "One-time costs incurred when closing (inventory spoilage, equipment storage, contract penalties). NAICS-driven defaults based on industry.",
  restartCosts: "One-time costs to resume operations (re-hiring, re-stocking, marketing). Typically higher than shutdown costs.",
  multiplier: "Some policies allow 2x damages. 'Profits Only' applies to net profit; 'Full Loss' applies to entire business interruption claim."
};

interface FutureClosureEdgeCasesProps {
  reportId: string;
  onConfigChange: (config: EdgeCaseConfig) => void;
  initialConfig?: EdgeCaseConfig;
  entityType?: string;  // From wizard
}

export default function FutureClosureEdgeCases({
  reportId,
  onConfigChange,
  initialConfig,
  entityType: wizardEntityType
}: FutureClosureEdgeCasesProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<EdgeCaseAnalysisResult | null>(null);
  
  // Edge case configuration state
  const [config, setConfig] = useState<EdgeCaseConfig>({
    exclude_spike: initialConfig?.exclude_spike ?? false,
    spike_year: initialConfig?.spike_year ?? null,
    excluded_quarters: initialConfig?.excluded_quarters ?? [],  // NEW: Quarter-specific exclusion
    accounting_basis: initialConfig?.accounting_basis ?? 'accrual',
    lag_days: initialConfig?.lag_days ?? 0,
    payroll_mode: initialConfig?.payroll_mode ?? 'fixed',
    icw_payroll: initialConfig?.icw_payroll ?? false,
    entity_type: initialConfig?.entity_type ?? wizardEntityType as any ?? 'restaurant',
    shutdown_pct: initialConfig?.shutdown_pct ?? null,
    restart_pct: initialConfig?.restart_pct ?? null,
    multiplier_mode: initialConfig?.multiplier_mode ?? 'none'
  });

  // AI Explanation state
  const [explanation, setExplanation] = useState<EdgeCaseExplanationResult | null>(null);
  const [generatingExplanation, setGeneratingExplanation] = useState(false);
  const [explanationExpanded, setExplanationExpanded] = useState(false);

  // Run analysis when expanded or config changes
  useEffect(() => {
    if (isExpanded && reportId) {
      runAnalysis();
    }
  }, [isExpanded, reportId]);

  // Notify parent of config changes
  useEffect(() => {
    onConfigChange(config);
  }, [config, onConfigChange]);

  const runAnalysis = async () => {
    setLoading(true);
    try {
      const result = await analyzeEdgeCases(reportId, config);
      setAnalysisResult(result);
    } catch (error) {
      console.error('Edge case analysis failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateConfig = (updates: Partial<EdgeCaseConfig>) => {
    setConfig(prev => ({ ...prev, ...updates }));
  };

  // Generate AI explanation
  const handleGenerateExplanation = async () => {
    setGeneratingExplanation(true);
    try {
      const result = await generateEdgeCaseExplanation(reportId, { edge_case_config: config });
      setExplanation(result);
      setExplanationExpanded(true);
    } catch (error) {
      console.error('Failed to generate explanation:', error);
    } finally {
      setGeneratingExplanation(false);
    }
  };

  const flags = analysisResult?.flags;
  const dualProjections = analysisResult?.dual_projections;

  return (
    <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden">
      {/* Collapsible Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition"
      >
        <div className="flex items-center gap-2">
          <Settings className="w-5 h-5 text-gray-600" />
          <span className="font-semibold text-gray-800">⚙️ Advanced Edge Case Overrides</span>
          {flags?.event_anomaly && (
            <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-800 text-xs rounded-full">
              Spike Detected
            </span>
          )}
          {flags?.missing_periods && flags.missing_periods.length > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-800 text-xs rounded-full">
              {flags.missing_periods.length} Data Gaps
            </span>
          )}
        </div>
        {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-4 space-y-6 bg-white">
          {loading && (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-gray-600">Analyzing data...</span>
            </div>
          )}

          {!loading && (
            <>
              {/* 1. Revenue Spike Detection */}
              {flags?.event_anomaly && (
                <div className="p-4 border-2 border-amber-200 rounded-lg bg-amber-50">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-amber-900">
                          {flags.event_anomaly.year} Revenue Spike Detected (+{flags.event_anomaly.spike_pct}% YoY)
                          {flags.event_anomaly.concentration && 
                           flags.event_anomaly.concentration !== 'full year' && 
                           flags.event_anomaly.concentration !== 'distributed across year' && (
                            <span className="ml-1 text-red-700 font-bold">
                              — {flags.event_anomaly.concentration}
                            </span>
                          )}
                        </p>
                        <Tooltip text={TOOLTIPS.revenueSpike}>
                          <HelpCircle className="w-4 h-4 text-amber-500 cursor-help" />
                        </Tooltip>
                      </div>
                      <p className="text-sm text-amber-700 mt-1">
                        {flags.event_anomaly.reason}
                      </p>
                      
                      {/* P2: Granular Quarter Breakdown */}
                      {flags.event_anomaly.granular_details && (
                        <div className="mt-2 p-2 bg-amber-100/70 rounded">
                          <p className="text-xs font-semibold text-amber-800 mb-1">📊 Quarterly Growth:</p>
                          <div className="flex gap-3 text-xs">
                            {Object.entries(flags.event_anomaly.granular_details.quarterly_growth || {}).map(([quarter, growth]: [string, number]) => {
                              const isConcentrated = flags.event_anomaly?.granular_details?.concentrated_quarters?.includes(quarter);
                              return (
                                <span 
                                  key={quarter}
                                  className={`px-2 py-0.5 rounded ${
                                    isConcentrated 
                                      ? 'bg-red-200 text-red-800 font-bold' 
                                      : 'bg-gray-100 text-gray-600'
                                  }`}
                                >
                                  {quarter}: {growth > 0 ? '+' : ''}{growth}%
                                </span>
                              );
                            })}
                          </div>
                          {flags.event_anomaly.granular_details.top_growth_months?.length > 0 && (
                            <p className="mt-1 text-xs text-amber-700">
                              Top months: {flags.event_anomaly.granular_details.top_growth_months
                                .map((m: GrowthMonth) => `${m.month_name} (+${m.growth_pct}%)`)
                                .join(', ')}
                            </p>
                          )}
                        </div>
                      )}
                      
                      {/* Exclude/Include Toggle + Quarter-Specific Selection */}
                      <div className="mt-3 space-y-3">
                        {/* Main toggle row */}
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              // When excluding, default to concentrated quarters if available
                              const defaultQuarters = flags.event_anomaly?.granular_details?.concentrated_quarters || [];
                              updateConfig({ 
                                exclude_spike: true, 
                                spike_year: flags.event_anomaly?.year,
                                excluded_quarters: defaultQuarters.length > 0 ? defaultQuarters : ['Q1', 'Q2', 'Q3', 'Q4']
                              });
                            }}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                              config.exclude_spike 
                                ? 'bg-red-500 text-white' 
                                : 'bg-red-100 text-red-800 hover:bg-red-200'
                            }`}
                          >
                            Exclude from Trend
                          </button>
                          <button
                            onClick={() => updateConfig({ exclude_spike: false, spike_year: null, excluded_quarters: [] })}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                              !config.exclude_spike 
                                ? 'bg-green-500 text-white' 
                                : 'bg-green-100 text-green-800 hover:bg-green-200'
                            }`}
                          >
                            Include (Keep As-Is)
                          </button>
                        </div>
                        
                        {/* Quarter-Specific Exclusion Checkboxes - Only show when exclude_spike is true and granular_details available */}
                        {config.exclude_spike && flags.event_anomaly?.granular_details?.quarterly_growth && (
                          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                              <p className="text-xs font-semibold text-red-800">
                                🎯 Select Quarters to Normalize (surgical exclusion):
                              </p>
                              <Tooltip text={TOOLTIPS.quarterExclusion}>
                                <HelpCircle className="w-3 h-3 text-red-500 cursor-help" />
                              </Tooltip>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {['Q1', 'Q2', 'Q3', 'Q4'].map((quarter) => {
                                const growth = flags.event_anomaly?.granular_details?.quarterly_growth?.[quarter] || 0;
                                const isConcentrated = flags.event_anomaly?.granular_details?.concentrated_quarters?.includes(quarter);
                                const isSelected = config.excluded_quarters?.includes(quarter);
                                
                                const toggleQuarter = () => {
                                  const currentQuarters = config.excluded_quarters || [];
                                  const newQuarters = isSelected
                                    ? currentQuarters.filter(q => q !== quarter)
                                    : [...currentQuarters, quarter];
                                  updateConfig({ excluded_quarters: newQuarters });
                                };
                                
                                return (
                                  <button
                                    key={quarter}
                                    onClick={toggleQuarter}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition border-2 ${
                                      isSelected
                                        ? 'bg-red-500 text-white border-red-600'
                                        : isConcentrated
                                          ? 'bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200'
                                          : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200'
                                    }`}
                                  >
                                    {quarter}: {growth > 0 ? '+' : ''}{growth}%
                                    {isSelected && ' ✓'}
                                    {isConcentrated && !isSelected && ' ⚠️'}
                                  </button>
                                );
                              })}
                            </div>
                            <p className="mt-2 text-xs text-gray-500">
                              {config.excluded_quarters?.length === 4 
                                ? '📋 All quarters selected (full year normalization)'
                                : config.excluded_quarters?.length === 0
                                  ? '⚠️ No quarters selected - select at least one'
                                  : `✅ Normalizing ${config.excluded_quarters?.join(', ')} only`
                              }
                            </p>
                          </div>
                        )}
                      </div>
                      
                      {/* Dual Projections */}
                      {dualProjections && dualProjections.sustainable_demand && (
                        <div className="mt-3 p-3 bg-white rounded border border-amber-200">
                          <p className="text-xs font-semibold text-gray-600 mb-2">Live Preview:</p>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-gray-600">Sustainable Baseline:</span>
                              <span className="ml-2 font-bold text-green-700">
                                ${dualProjections.sustainable_demand.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-600">Event-Included:</span>
                              <span className="ml-2 font-bold text-amber-700">
                                ${dualProjections.spiked_demand?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* 2. Accounting Basis */}
              <div className="p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-4 h-4 text-gray-600" />
                  <label className="font-medium text-gray-800">Accounting Basis Override</label>
                  <Tooltip text={TOOLTIPS.accountingBasis}>
                    <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                  </Tooltip>
                </div>
                <div className="flex items-center gap-4">
                  <select
                    value={config.accounting_basis}
                    onChange={(e) => updateConfig({ 
                      accounting_basis: e.target.value as 'accrual' | 'cash',
                      lag_days: e.target.value === 'accrual' ? 0 : config.lag_days 
                    })}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="accrual">Accrual (Default)</option>
                    <option value="cash">Cash Basis</option>
                  </select>
                  
                  {config.accounting_basis === 'cash' && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Apply lag:</span>
                      <select
                        value={config.lag_days}
                        onChange={(e) => updateConfig({ lag_days: parseInt(e.target.value) })}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      >
                        <option value={0}>No adjustment</option>
                        <option value={15}>15-day lag</option>
                        <option value={30}>30-day lag</option>
                        <option value={45}>45-day lag</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {/* 3. Payroll Classification */}
              <div className="p-4 border-2 border-blue-200 rounded-lg bg-blue-50">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-blue-600" />
                  <label className="font-medium text-blue-900">Treat Payroll As:</label>
                  <Tooltip text={TOOLTIPS.payrollMode}>
                    <HelpCircle className="w-4 h-4 text-blue-400 cursor-help" />
                  </Tooltip>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => updateConfig({ payroll_mode: 'fixed' })}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                      config.payroll_mode === 'fixed' 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                    }`}
                  >
                    Continue All Staff (Attorney Mode)
                  </button>
                  <button
                    onClick={() => updateConfig({ payroll_mode: 'variable', icw_payroll: false })}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                      config.payroll_mode === 'variable' 
                        ? 'bg-orange-600 text-white' 
                        : 'bg-orange-100 text-orange-800 hover:bg-orange-200'
                    }`}
                  >
                    Suspend Non-Essential (Carrier Mode)
                  </button>
                </div>
                
                {config.payroll_mode === 'fixed' && (
                  <label className="mt-3 flex items-center gap-2 text-sm text-blue-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.icw_payroll}
                      onChange={(e) => updateConfig({ icw_payroll: e.target.checked })}
                      className="rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                    />
                    Flag as Increased Cost of Working (ICW)
                    <Tooltip text={TOOLTIPS.icwPayroll}>
                      <HelpCircle className="w-3 h-3 text-blue-400 cursor-help" />
                    </Tooltip>
                  </label>
                )}
              </div>

              {/* 4. Entity Type */}
              <div className="p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Building className="w-4 h-4 text-gray-600" />
                  <label className="font-medium text-gray-800">Business Type:</label>
                  <Tooltip text={TOOLTIPS.entityType}>
                    <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                  </Tooltip>
                </div>
                <select
                  value={config.entity_type}
                  onChange={(e) => updateConfig({ entity_type: e.target.value as any })}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="restaurant">Restaurant</option>
                  <option value="retail">Retail</option>
                  <option value="factory">Factory/Manufacturing</option>
                  <option value="nonprofit">Nonprofit</option>
                </select>
                
                {config.entity_type === 'nonprofit' && (
                  <p className="mt-2 text-xs text-yellow-700 bg-yellow-50 p-2 rounded flex items-center gap-1">
                    <Info className="w-3 h-3" />
                    80% expenses treated as variable by default (vs 65% for standard businesses)
                  </p>
                )}
              </div>

              {/* 5. Data Gaps Warning */}
              {flags?.missing_periods && flags.missing_periods.length > 0 && (
                <div className="p-4 border-2 border-red-200 rounded-lg bg-red-50">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-red-900">🔴 Data Gaps Detected</p>
                      <ul className="mt-2 text-sm text-red-700 space-y-1">
                        {flags.missing_periods.map((gap, i) => (
                          <li key={i}>
                            • {gap.period} missing — using BLS NAICS {gap.naics} proxy (${gap.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/mo)
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* 6. Shutdown/Restart Costs */}
              <div className="p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <DollarSign className="w-4 h-4 text-gray-600" />
                  <label className="font-medium text-gray-800">Closure Cost Overrides</label>
                  <span className="text-xs text-gray-500">
                    (Default: NAICS {config.entity_type})
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1 flex items-center gap-1">
                      Shutdown Cost (% of revenue)
                      <Tooltip text={TOOLTIPS.shutdownCosts}>
                        <HelpCircle className="w-3 h-3 text-gray-400 cursor-help" />
                      </Tooltip>
                    </label>
                    <input
                      type="number"
                      placeholder={`${((flags?.closure_costs?.shutdown_pct ?? 0.15) * 100).toFixed(0)}% (default)`}
                      value={config.shutdown_pct !== null ? (config.shutdown_pct * 100) : ''}
                      onChange={(e) => updateConfig({ 
                        shutdown_pct: e.target.value ? parseFloat(e.target.value) / 100 : null 
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      min={0}
                      max={100}
                      step={1}
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1 flex items-center gap-1">
                      Restart Cost (% of revenue)
                      <Tooltip text={TOOLTIPS.restartCosts}>
                        <HelpCircle className="w-3 h-3 text-gray-400 cursor-help" />
                      </Tooltip>
                    </label>
                    <input
                      type="number"
                      placeholder={`${((flags?.closure_costs?.restart_pct ?? 0.40) * 100).toFixed(0)}% (default)`}
                      value={config.restart_pct !== null ? (config.restart_pct * 100) : ''}
                      onChange={(e) => updateConfig({ 
                        restart_pct: e.target.value ? parseFloat(e.target.value) / 100 : null 
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      min={0}
                      max={100}
                      step={1}
                    />
                  </div>
                </div>
              </div>

              {/* 7. Multiplier */}
              <div className="p-4 border border-blue-200 rounded-lg bg-blue-50">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-4 h-4 text-blue-600" />
                  <label className="font-medium text-blue-900">Multiplier Application</label>
                  <Tooltip text={TOOLTIPS.multiplier}>
                    <HelpCircle className="w-4 h-4 text-blue-400 cursor-help" />
                  </Tooltip>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => updateConfig({ multiplier_mode: 'none' })}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                      config.multiplier_mode === 'none' 
                        ? 'bg-gray-600 text-white' 
                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                    }`}
                  >
                    None (1×)
                  </button>
                  <button
                    onClick={() => updateConfig({ multiplier_mode: 'profits_only' })}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                      config.multiplier_mode === 'profits_only' 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                    }`}
                  >
                    2× Lost Profits Only
                  </button>
                  <button
                    onClick={() => updateConfig({ multiplier_mode: 'full_loss' })}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                      config.multiplier_mode === 'full_loss' 
                        ? 'bg-indigo-600 text-white' 
                        : 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200'
                    }`}
                  >
                    2× Full Loss
                  </button>
                </div>
                {config.multiplier_mode !== 'none' && (
                  <p className="mt-2 text-xs text-blue-700">
                    {config.multiplier_mode === 'profits_only' 
                      ? '2× multiplier applied to lost gross profit only (standard attorney position)'
                      : '2× multiplier applied to total demand including shutdown/restart costs'}
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                {/* Re-analyze Button */}
                <button
                  onClick={runAnalysis}
                  disabled={loading}
                  className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium rounded-lg transition flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Settings className="w-4 h-4" />
                      Re-analyze
                    </>
                  )}
                </button>
                
                {/* Generate AI Explanation Button */}
                <button
                  onClick={handleGenerateExplanation}
                  disabled={generatingExplanation || !flags}
                  className="flex-1 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-medium rounded-lg transition flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {generatingExplanation ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Generate AI Explanation
                    </>
                  )}
                </button>
              </div>
              
              {/* AI Explanation Display */}
              {explanation && (
                <div className="mt-4 border-2 border-blue-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setExplanationExpanded(!explanationExpanded)}
                    className="w-full px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 flex items-center justify-between hover:from-blue-100 hover:to-indigo-100 transition"
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-blue-600" />
                      <span className="font-medium text-blue-900">AI-Generated Explanation</span>
                      <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                        {explanation.word_count} words
                      </span>
                    </div>
                    {explanationExpanded ? (
                      <ChevronUp className="w-5 h-5 text-blue-600" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-blue-600" />
                    )}
                  </button>
                  
                  {explanationExpanded && (
                    <div className="p-4 bg-white">
                      {/* Summary */}
                      {explanation.summary && (
                        <div className="mb-4 p-3 bg-blue-50 border-l-4 border-blue-500 rounded-r-lg">
                          <p className="text-sm font-medium text-blue-900">Summary</p>
                          <p className="text-sm text-blue-800 mt-1">{explanation.summary}</p>
                        </div>
                      )}
                      
                      {/* Full Explanation */}
                      <div className="prose prose-sm max-w-none">
                        {explanation.explanation_text.split('\n\n').map((paragraph, idx) => (
                          <p key={idx} className="text-gray-700 mb-3 whitespace-pre-wrap">
                            {paragraph}
                          </p>
                        ))}
                      </div>
                      
                      {/* Copy Button */}
                      <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
                        <span className="text-xs text-gray-500">
                          Generated by {explanation.generated_by}
                        </span>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(explanation.explanation_text);
                          }}
                          className="px-3 py-1.5 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition flex items-center gap-1"
                        >
                          📋 Copy to Clipboard
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
